import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ImageCropper = ({ onCropComplete, onCancel, existingImageUrl, parentToken }) => {
  const { token: contextToken } = useAuth();
  const token = parentToken || contextToken || localStorage.getItem('adminToken');
  
  const [src, setSrc] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [useDirectLoad, setUseDirectLoad] = useState(true);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  // Try to load the image directly first (works for Cloudinary and some other hosts)
  useEffect(() => {
    if (existingImageUrl && useDirectLoad) {
      console.log('Attempting direct image load:', existingImageUrl);
      setIsLoadingImage(true);
      
      // Create a test image to check if direct loading works
      const testImg = new Image();
      testImg.crossOrigin = 'anonymous';
      
      testImg.onload = () => {
        console.log('Direct image load successful');
        setSrc(existingImageUrl);
        setIsLoadingImage(false);
      };
      
      testImg.onerror = () => {
        console.log('Direct load failed, trying proxy...');
        setUseDirectLoad(false);
        tryProxyLoad();
      };
      
      testImg.src = existingImageUrl;
    }
  }, [existingImageUrl, useDirectLoad]);

  // Fallback to proxy if direct load fails
  const tryProxyLoad = async () => {
    if (!token) {
      console.log('No token available for proxy');
      setLoadError('Please select the image file to crop');
      setIsLoadingImage(false);
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const encodedUrl = encodeURIComponent(existingImageUrl);
      const proxyUrl = `${apiUrl}/api/proxy-image?url=${encodedUrl}`;
      
      console.log('Fetching via proxy:', proxyUrl);
      
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.dataUrl) {
          console.log('Proxy load successful');
          setSrc(data.dataUrl);
        } else {
          throw new Error('No data URL in response');
        }
      } else {
        throw new Error(`Proxy returned ${response.status}`);
      }
    } catch (error) {
      console.error('Proxy load failed:', error);
      setLoadError('Please select the image file to crop');
    } finally {
      setIsLoadingImage(false);
    }
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setLoadError(null);
      const reader = new FileReader();
      reader.addEventListener('load', () => setSrc(reader.result));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height) * 0.8;
    setCrop({
      unit: 'px',
      width: cropSize,
      height: cropSize * 1.2,
      x: (width - cropSize) / 2,
      y: 0
    });
  }, []);

  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    
    try {
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create image blob'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', 0.95);
      });
    } catch (error) {
      // Canvas tainted error - need to reload via proxy or file
      console.error('Canvas tainted:', error);
      setLoadError('Cannot crop this image directly. Please select the file to crop.');
      setSrc(null);
      return null;
    }
  }, [completedCrop]);

  const handleCropComplete = async () => {
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
      }
    } catch (error) {
      if (error.message.includes('tainted') || error.message.includes('insecure')) {
        setLoadError('Cannot crop this image directly. Please select the file to crop.');
        setSrc(null);
      } else {
        alert('Error cropping image. Please try again.');
      }
      console.error('Crop error:', error);
    }
  };

  return (
    <div className="image-cropper-modal">
      <div className="image-cropper-content">
        <h3>Crop Your Image</h3>
        <p className="cropper-hint">Drag to select the area you want to keep</p>
        
        {isLoadingImage && (
          <div className="cropper-loading">
            <span>Loading image...</span>
          </div>
        )}

        {(loadError || (!src && !isLoadingImage)) && (
          <div className="cropper-upload-area">
            {existingImageUrl && (
              <div className="cropper-existing-preview">
                <p style={{ marginBottom: '8px', color: '#666', fontSize: '14px' }}>Current image:</p>
                <img 
                  src={existingImageUrl} 
                  alt="Current" 
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', marginBottom: '16px' }}
                />
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px', maxWidth: '300px', textAlign: 'center' }}>
                  To crop this image, please select the original photo file from your device:
                </p>
              </div>
            )}
            {loadError && !existingImageUrl && <p className="cropper-error-text">{loadError}</p>}
            <label className="cropper-upload-btn">
              <input
                type="file"
                accept="image/*"
                onChange={onSelectFile}
                hidden
              />
              <span>Select Photo to Crop</span>
            </label>
          </div>
        )}

        {src && !isLoadingImage && (
          <>
            <div className="cropper-preview-area">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                minWidth={50}
                minHeight={50}
                keepSelection={true}
                ruleOfThirds={true}
              >
                <img
                  ref={imgRef}
                  src={src}
                  alt="Crop preview"
                  onLoad={onImageLoad}
                  crossOrigin="anonymous"
                  style={{ maxHeight: '400px', maxWidth: '100%', display: 'block' }}
                />
              </ReactCrop>
            </div>
            
            <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', margin: '12px 0' }}>
              Drag on the image to select the area you want to keep
            </p>

            <div className="cropper-actions">
              <button 
                type="button" 
                onClick={() => {
                  setSrc(null);
                  setLoadError(null);
                }} 
                className="cropper-btn-secondary"
              >
                Choose Different Image
              </button>
              <button 
                type="button" 
                onClick={handleCropComplete}
                className="cropper-btn-primary"
                disabled={!completedCrop}
              >
                Apply Crop & Upload
              </button>
            </div>
          </>
        )}

        <button 
          type="button" 
          onClick={onCancel} 
          className="cropper-close-btn"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default ImageCropper;
